import { useCallback, useEffect, useState } from "react";
import { ethers } from "ethers";
import { fetchDepositsByOwner } from "../services/api.js";

// Lifecycle state numeric enum mirrored from the IGilderTypes contract.
// Re-exported so callers can patch optimistically without hardcoding ints.
export const DEPOSIT_STATE = Object.freeze({
    None: 0,
    Active: 1,
    Matured: 2,
    Dormant: 3,
    Abandoned: 4,
    Exited: 5,
    Closed: 6
});

/**
 * Returns every deposit owned by `address`, plus a `refresh()` callback.
 *
 * Architecture (per data-flow spec):
 *   blockchain → backend indexer/backfill → backend Mongo → API → frontend
 *
 * The frontend NEVER reads the chain directly for data display. Writes
 * (openDeposit, settleMatured, etc.) still go through the user's wallet
 * → contract, but the resulting state is read back via the API after
 * the write triggers a /api/deposits/:id/sync ping.
 */
export function useDeposits(address) {
    const [deposits, setDeposits] = useState([]);
    // Start true so the very first render — before the useEffect below
    // has had a chance to fire — already paints shimmer placeholders.
    // Without this the table would briefly flash an empty "No positions"
    // state on mount before the fetch even started.
    const [isLoading, setLoading] = useState(Boolean(address));
    const [error, setError] = useState(null);

    // `opts.background === true` marks a silent poll-driven refresh: it
    // keeps the currently rendered values on screen while re-fetching so
    // the UI never flips back to shimmer placeholders (which caused the
    // "page jumps every 15s" layout jitter). Only foreground calls — the
    // initial mount and address change — toggle the loading flag.
    const refresh = useCallback(async (opts = {}) => {
        const background = opts.background === true;
        if (!address) {
            setDeposits([]);
            setLoading(false);
            return;
        }
        if (!background) {
            setLoading(true);
            setError(null);
        }
        try {
            const data = await fetchDepositsByOwner(address);
            const items = (data?.items || []).map(normaliseFromApi);
            setDeposits(items);
            setError(null);
        } catch (err) {
            // No chain fallback — by spec the API is the only read path.
            // If the backend is down the UI shows the error and the user
            // can retry; we don't want to silently drift between two
            // sources of truth. On a background poll we keep the last good
            // rows rather than blanking them — a transient blip shouldn't
            // wipe the table the user is looking at.
            setError(err?.message || "Failed to load deposits from API");
            if (!background) setDeposits([]);
        } finally {
            if (!background) setLoading(false);
        }
    }, [address]);

    useEffect(() => {
        // Reset to loading immediately when the address changes so the
        // shimmer is visible even before the new fetch starts. Otherwise
        // the previous wallet's deposit list would briefly stay rendered
        // while the in-flight fetch resolves.
        if (address) {
            setLoading(true);
            setDeposits([]);
        }
        refresh();
    }, [address, refresh]);

    // Background poll every 15s. Each page's `useDeposits` instance is
    // independent, so the Sidebar / MobileDrawer's Investment Summary
    // would otherwise stay stuck on its mount-time snapshot until the
    // user reloaded — even after a deposit had been written to Mongo by
    // the action flow's awaited sync POST. A modest poll keeps every
    // panel converged within ~15s of any change, no reload needed.
    useEffect(() => {
        if (!address) return undefined;
        const id = setInterval(() => {
            refresh({ background: true });
        }, 15_000);
        return () => clearInterval(id);
    }, [address, refresh]);

    /**
     * Optimistically merges `updates` into the deposit row matching `id`.
     * Used after a successful on-chain mutation (early exit, settle,
     * compound mode change) so the UI moves the row to its new state
     * immediately instead of waiting for the next indexer tick.
     * Background callers should still trigger `refresh()` afterwards so
     * the canonical backend state eventually replaces the optimistic
     * patch.
     */
    const patchDeposit = useCallback((id, updates) => {
        setDeposits((prev) =>
            prev.map((d) => (d.id === id ? { ...d, ...updates } : d))
        );
    }, []);

    return { deposits, isLoading, error, refresh, patchDeposit };
}

/**
 * Backend rows store every numeric on-chain value as a base-10 string so
 * BigInts survive JSON round-trips. BigInt() accepts the string
 * representation directly.
 */
function normaliseFromApi(raw) {
    return {
        id: Number(raw.depositId),
        owner: raw.owner,
        principal: raw.principal,
        accruedInterest: raw.accruedInterest,
        // Running total of interest ever consumed via auto-compound on
        // this source deposit (incremented by the indexer's
        // `onCompoundExecuted` handler). The "Accrued Interest" column
        // displays pending = liveAccrued − realizedInterest so the figure
        // drops to ~0 immediately after a successful compound and only
        // climbs back as fresh time elapses past the realization. Missing
        // this normalisation hop made `liveAccruedInterest(...)` see
        // `undefined → 0` and keep showing the full theoretical accrual
        // — which is what surfaced the "Compound failed: TriggerNotMet(0,
        // 100000000)" error: the button stayed enabled while on-chain
        // pending was already zero.
        realizedInterest: raw.realizedInterest || "0",
        loanBalance: raw.loanBalance,
        // Interest owed on the loan drawn against THIS deposit.
        //
        // §M6 — the chain services the loan out of bond yield BEFORE it
        // will release a withdrawal (BondContract._withdrawInterestFor →
        // _netAgainstOpenLoan). So any "what can I actually take out"
        // figure has to net this off first; quoting gross accrual is what
        // made every Max withdrawal revert.
        loanAccruedInterest: raw.loanAccruedInterest || "0",
        startTime: Number(raw.startTime),
        maturityTime: Number(raw.maturityTime),
        state: Number(raw.state),
        compoundMode: Number(raw.compoundMode),
        // §V6.2 — Turbo product fields. Immutable for the life of the
        // deposit; drive the Type pill, early-exit preview, and APR
        // displays. productMode 0 = Standard, 1 = Turbo.
        productMode: Number(raw.productMode || 0),
        earlyExitPenaltyBps: Number(raw.earlyExitPenaltyBps || 0),
        annualRateBps: Number(raw.annualRateBps || 0),
        // §V6.2 — on-chain capital routing snapshot. Both Standard and
        // Turbo split 80/10/9/1; surfacing the
        // STORED amount lets the TD detail page display the actual
        // chain truth instead of recomputing with hardcoded bps that
        // would mis-quote Turbo positions.
        safeVaultAmount: raw.safeVaultAmount || "0",
        tokenBuyAmount: raw.tokenBuyAmount || "0",
        treasuryAmount: raw.treasuryAmount || "0",
        liquidityAmount: raw.liquidityAmount || "0",
        // Chain block-timestamp of the close (set by indexer's
        // onDepositSettled / onEarlyExit) — null for live positions.
        // Used by TdDetail to label terminal-state deposits with their
        // actual end date instead of the now-misleading maturityTime.
        closedAt: raw.closedAt ? Number(raw.closedAt) : null,
        // The API row carries the txHash captured by the indexer (or
        // patched in by the periodic backfill). Forward it so the table
        // / detail page can show the real opening transaction.
        txHash: raw.txHash || ""
    };
}

export { ethers };
